import React from 'react'
import { Tag, Tooltip } from 'antd'

/**
 * Per-candidate invitation delivery status.
 *
 * This column answers one question — did the invitation reach the candidate? —
 * so it has four answers. Attempt progress (started / completed) is the STATUS
 * column's job; showing it here made one row state the same fact twice, in two
 * different vocabularies.
 *
 * `Sent` means a provider confirmed delivery. `Processing` means we handed the
 * message over and are still waiting to hear back — the two used to be the same
 * grey tag, which is how a bounced invite could sit there reading Sent.
 *
 * Colour carries urgency, not stage: only `failed` is red, because it is the only
 * status that means someone has to go and do something. `Processing` stays the
 * muted default rather than a warning colour — waiting is normal, not a problem.
 *
 * An invite can go out over email and WhatsApp, and the tag reports the candidate,
 * not the channel: reached on either one reads Sent, and Failed means every
 * attempted channel failed. The per-channel detail lives in the tooltip, because
 * a row reading Sent on the strength of WhatsApp is still hiding a bounced email
 * that somebody has to chase.
 */
const DELIVERY_STATUS_META = {
  processing: {
    color: 'default',
    label: 'Processing',
    hint: 'Invitation submitted, waiting for the provider to confirm delivery',
  },
  sent: {
    color: 'blue',
    label: 'Sent',
    hint: 'Provider confirmed the invitation was delivered',
  },
  failed: {
    color: 'red',
    label: 'Failed',
    // Reason is appended at render time when the API supplies one.
    hint: "Couldn't send invitation",
  },
  opened: {
    color: 'green',
    label: 'Opened',
    hint: 'Clicked the link from Email/Whatsapp invite',
  },
}

/**
 * Statuses this component used to render, now folded into the three above.
 *
 * Kept because a rolling deploy can put this bundle in front of an older API
 * that still returns them — without the aliases those rows would fall through to
 * the "not tracked" dash and wrongly read as having no delivery data at all.
 * Every one of them implies the candidate opened the link.
 */
const LEGACY_STATUS_ALIASES = {
  clicked: 'opened',
  started: 'opened',
  completed: 'opened',
}

/** Display names for the channels the API reports. */
const CHANNEL_LABELS = {
  email: 'Email',
  whatsapp: 'WhatsApp',
}

/** Per-channel outcome wording. Kept separate from the tag's own labels: the tag
 * describes the candidate, these describe one leg of the send.
 *
 * `sent` is retained as an alias of `delivered` for the same rolling-deploy
 * reason as LEGACY_STATUS_ALIASES: an older API reported every reached leg as
 * `sent`, and those rows must not render as a raw status key. */
const CHANNEL_STATUS_LABELS = {
  delivered: 'Sent',
  accepted: 'Processing',
  sent: 'Sent',
  failed: 'Failed',
}

/**
 * One "Email — Failed: 550 mailbox unavailable" line.
 *
 * An unrecognised channel falls back to its raw key rather than being dropped, so
 * a channel added on the API side shows up here — undescribed but visible —
 * instead of silently going missing from the breakdown.
 */
const ChannelLine = ({ channel, status, reason }) => {
  const name = CHANNEL_LABELS[channel] || channel
  const outcome = CHANNEL_STATUS_LABELS[status] || status
  return (
    <div>
      {name} — {outcome}
      {status === 'failed' && reason ? `: ${reason}` : ''}
    </div>
  )
}

/**
 * Renders nothing but a muted dash when a candidate has no tracking rows, which
 * is the honest answer for campaigns sent before delivery tracking existed. A
 * coloured tag there would assert something we never observed.
 */
const NO_INFO_HINT = 'No info available'

const DeliveryStatusTag = ({ status, label, reason, channels }) => {
  const resolved = DELIVERY_STATUS_META[status]
    ? status
    : LEGACY_STATUS_ALIASES[status]
  const meta = DELIVERY_STATUS_META[resolved]

  if (!meta) {
    return (
      <Tooltip title={NO_INFO_HINT}>
        <span style={{ color: '#C4C7D0' }}>—</span>
      </Tooltip>
    )
  }

  const channelLines = Array.isArray(channels) ? channels : []

  // With a per-channel breakdown the reason is already on the line that owns it,
  // so appending it to the summary too would print it twice. Without one — an
  // older API, or a row predating channel tracking — the summary is the only
  // place it can appear.
  const hint =
    resolved === 'failed' && reason && !channelLines.length
      ? `${meta.hint}: ${reason}`
      : meta.hint

  const title = channelLines.length ? (
    <div>
      <div>{hint}</div>
      <div style={{ marginTop: 4 }}>
        {channelLines.map((entry) => (
          <ChannelLine
            key={entry.channel}
            channel={entry.channel}
            status={entry.status}
            reason={entry.reason}
          />
        ))}
      </div>
    </div>
  ) : (
    hint
  )

  // The local label wins for a status we aliased — an older API's "Completed"
  // must not leak through as text after we mapped it to Opened. Otherwise the
  // server label wins so the tag and the Excel export read identically.
  const text = (resolved === status && label) || meta.label || status

  return (
    <Tooltip title={title}>
      <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>
        {text}
      </Tag>
    </Tooltip>
  )
}

export { DELIVERY_STATUS_META, LEGACY_STATUS_ALIASES, CHANNEL_LABELS }
export default DeliveryStatusTag
